/**
 * `wayai bases …` — the base entity, plus the two DEPRECATED aliases (`use`,
 * `unbind`) that had to be namespaced back when the worktree tripwire was two
 * single-valued files. It is one set-valued scope now, so `wayai use bases/<id>`
 * and `wayai unbind bases/<id>` are the commands; these two remain only as a
 * published output shape. `promote` keeps its own word, distinct from
 * `wayai publish`, because promoting a base and publishing a hub are different
 * operations on different entities.
 */

import { Command } from 'commander';
import * as fs from 'node:fs';
import * as yaml from 'js-yaml';
import { buildBasesBatchCommand } from './batch.js';
import { buildBasesImportCommand } from './import.js';
import { buildBasesProvidersCommand } from './providers.js';
import { buildBasesReportCommand } from './report.js';
import { buildBasesCredentialsCommand } from './credentials.js';
import { buildBasesSqlCommand } from './sql.js';
import { buildBasesTokensCommand } from './tokens.js';
import { createDataClient } from '../client.js';
import { resolveBaseTags } from '../base-tags.js';
import { printOutput } from '../output.js';
import { basePathSegment, outputFormat, parseData, pathSegment, splitList } from '../helpers.js';
import { confirm } from '../../lib/utils.js';
import { sanitizeTerminalText } from '../../lib/terminal-output.js';
import { findGitRoot } from '../../lib/workspace.js';
import { resolveBaseSelectorToId } from '../../lib/base-workspace.js';
import { readScope, setScopeAxis } from '../../lib/worktree-scope.js';
import { expected } from '../../lib/expected.js';

/** Only the field this command reads back; the response carries more. */
interface BaseSettings {
  settings?: Record<string, unknown>;
}

/**
 * Append the pagination cursor to a listing path.
 *
 * The three listing commands each follow every page rather than printing the
 * server's first one: a truncated list reads as "this base does not exist" to a
 * script, and for `promotions` — append-only, so it crosses a page boundary in
 * normal use — a truncated history omits the id `rollback --promotion <id>`
 * needs.
 */
function pageOf(path: string, cursor: string | undefined): string {
  if (!cursor) return path;
  return `${path}${path.includes('?') ? '&' : '?'}cursor=${encodeURIComponent(cursor)}`;
}

/** One of `allowed`, or a thrown error naming the flag and its values. */
function parseEnum<T extends string>(
  flag: string,
  value: string | undefined,
  allowed: readonly T[],
): T | undefined {
  if (value === undefined) return undefined;
  if (!(allowed as readonly string[]).includes(value)) {
    throw expected(`${flag} must be one of: ${allowed.join(', ')}.`);
  }
  return value as T;
}

export function buildBasesCommand(): Command {
  const bases = new Command('bases').description('Manage bases');

  bases
    .command('list')
    .description('List all bases')
    .option('--tag <tag>', 'Filter by tag')
    .action(async function (this: Command, opts: { tag?: string }) {
      const client = await createDataClient();
      const qs = opts.tag ? `?tag=${encodeURIComponent(opts.tag)}` : '';
      printOutput(
        await client.collectPages((cursor) => pageOf(`/v1/bases${qs}`, cursor)),
        outputFormat(this),
      );
    });

  bases
    .command('get <id>')
    .description('Get a base')
    .action(async function (this: Command, id: string) {
      const client = await createDataClient();
      // `pathSegment`, not `basePathSegment`: a read is one of the things the API
      // still serves for a base whose id predates its rule, so the CLI must not
      // be the layer that refuses it.
      printOutput(await client.request('GET', `/v1/bases/${pathSegment(id)}`), outputFormat(this));
    });

  bases
    .command('create <id>')
    .description('Create a base')
    .requiredOption('--name <name>', 'Base name')
    .option('--description <desc>', 'Description')
    .option('--tags <tags>', 'Comma-separated organization tag names (e.g. clinica-medica,billing)')
    .option(
      '--timezone <tz>',
      "Default IANA timezone for datetime canonicalization (e.g. America/Sao_Paulo); a record type's own timezone overrides it",
    )
    .option(
      '--environment <env>',
      'production (default) | preview. Fixed for the life of a base and not changeable on one that exists — but not welded to the id: once a base is permanently deleted and that deletion completes, the id is free and can be created again in either environment, so a preview you prototyped on can become the production base under the same slug. For a base you intend to keep, take the default so the durable id belongs to production, then get a linked preview with create-preview and promote from it. Pass preview for an ephemeral or eval base under a derived id: a preview created here has no production origin, cannot be given one later, and so can never be promoted directly',
    )
    .action(async function (
      this: Command,
      id: string,
      opts: {
        name: string;
        description?: string;
        tags?: string;
        timezone?: string;
        environment?: string;
      },
    ) {
      const environment = parseEnum('--environment', opts.environment, [
        'production',
        'preview',
      ] as const);
      // Validated before the client is built and before `resolveBaseTags` issues
      // its request: an id the API will refuse must cost no round trip, which is
      // the whole point of stating the rule client-side.
      const target = basePathSegment(id);
      const client = await createDataClient();
      const body: Record<string, unknown> = { name: opts.name, description: opts.description };
      if (environment) body.environment = environment;
      if (opts.tags) body.tags = await resolveBaseTags(opts.tags, client);
      if (opts.timezone) body.settings = { timezone: opts.timezone };
      printOutput(await client.request('PUT', `/v1/bases/${target}`, body), outputFormat(this));
    });

  bases
    .command('update <id>')
    .description(
      "Update a base's mutable config (display name, description, tags, default timezone/settings). The id/slug is immutable.",
    )
    .option('--name <name>', 'New display name')
    .option('--description <desc>', 'Description')
    .option(
      '--tags <tags>',
      'Comma-separated organization tag names — replaces the existing tags. An empty value clears them',
    )
    .option(
      '--timezone <tz>',
      'Default IANA timezone for datetime canonicalization. Merges into existing settings.',
    )
    .option(
      '--settings <json>',
      'Raw settings JSON object (inline JSON or @file.json) — replaces the entire settings object',
    )
    .option(
      '--integrations <mode>',
      'enabled | disabled. Preview-only: `disabled` neutralizes every external integration edge so the seeded preview behaves natively for production-safe agent evals',
    )
    .action(async function (
      this: Command,
      id: string,
      opts: {
        name?: string;
        description?: string;
        tags?: string;
        timezone?: string;
        settings?: string;
        integrations?: string;
      },
    ) {
      const integrations = parseEnum('--integrations', opts.integrations, ['enabled', 'disabled'] as const);
      if (
        opts.name === undefined &&
        opts.description === undefined &&
        opts.tags === undefined &&
        opts.timezone === undefined &&
        opts.settings === undefined &&
        integrations === undefined
      ) {
        throw expected(
          'Nothing to update. Provide at least one of --name, --description, --tags, --timezone, --settings, --integrations.',
        );
      }

      // Ahead of the client and of `resolveBaseTags`, for the same reason that
      // resolution is itself ahead of the settings read-modify-write below: a
      // value the API will refuse must leave nothing half-applied and cost no
      // round trip. Computed once — this command reaches the id twice.
      const target = basePathSegment(id);
      const client = await createDataClient();
      const body: Record<string, unknown> = {};
      if (opts.name !== undefined) body.name = opts.name;
      if (opts.description !== undefined) body.description = opts.description;
      // Resolved BEFORE the settings read-modify-write below, so a mistyped tag name costs
      // no round trip and can leave nothing half-applied.
      if (opts.tags !== undefined) body.tags = await resolveBaseTags(opts.tags, client);
      if (integrations !== undefined) body.integrations = integrations;

      if (opts.settings !== undefined || opts.timezone !== undefined) {
        // The update replaces `settings` wholesale, so build the full object.
        // `--settings` is the raw base; `--timezone` merges on top — and when it
        // is alone, into the EXISTING settings (one read-modify-write) so it
        // doesn't clobber keys the caller never mentioned.
        let settings: Record<string, unknown>;
        if (opts.settings !== undefined) {
          let parsed: Record<string, unknown>;
          try {
            parsed = parseData(opts.settings);
          } catch (e) {
            // parseData both reads the file (for `@file.json`) and JSON-parses,
            // so surface the underlying reason rather than masking a missing
            // file and a syntax error as the same message.
            throw expected(`--settings: ${e instanceof Error ? e.message : 'could not be read as JSON'}`);
          }
          if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
            throw expected('--settings must be a valid JSON object');
          }
          settings = parsed;
        } else {
          const existing = await client.request<BaseSettings>('GET', `/v1/bases/${target}`);
          settings = { ...(existing?.settings ?? {}) };
        }
        if (opts.timezone !== undefined) settings.timezone = opts.timezone;
        body.settings = settings;
      }

      printOutput(await client.request('PUT', `/v1/bases/${target}`, body), outputFormat(this));
    });

  bases
    .command('rename <id>')
    .description(
      "Rename a base's display name (the id/slug is immutable — it scopes token grants, MCP endpoints, and references)",
    )
    .requiredOption('--name <name>', 'New display name')
    .action(async function (this: Command, id: string, opts: { name: string }) {
      const target = basePathSegment(id);
      const client = await createDataClient();
      printOutput(
        await client.request('PUT', `/v1/bases/${target}`, { name: opts.name }),
        outputFormat(this),
      );
    });

  bases
    .command('tag <id>')
    .description("Set tags on a base, from the organization's tag list")
    .requiredOption(
      '--tags <tags>',
      'Comma-separated organization tag names (e.g. clinica-medica,billing). An empty value clears them',
    )
    .action(async function (this: Command, id: string, opts: { tags: string }) {
      const target = basePathSegment(id);
      const client = await createDataClient();
      const tags = await resolveBaseTags(opts.tags, client);
      printOutput(
        await client.request('PUT', `/v1/bases/${target}`, { tags }),
        outputFormat(this),
      );
    });

  bases
    .command('delete <id>')
    .description(
      'Delete a base. By default this is a tombstone: the base stops being listed, but its storage is retained and recreating the id restores it. Add --purge (preview bases only) to also reclaim the storage',
    )
    .option('-y, --yes', 'Skip confirmation prompt')
    .option(
      '--purge',
      'Preview bases only. Permanently destroy the stored records, relationships, file metadata and config of this base instead of tombstoning it. The id is retired until the deletion finishes, then frees itself',
    )
    .action(async function (this: Command, id: string, opts: { yes?: boolean; purge?: boolean }) {
      const prompt = opts.purge
        ? `Purge base "${id}"? Its records, relationships, file metadata and config are destroyed permanently. The id "${id}" is retired until the deletion finishes, and frees itself afterwards — tokens scoped to this base stop working on it either way.`
        : `Delete base "${id}"? This cannot be undone.`;
      if (!opts.yes && !(await confirm(prompt))) {
        console.log('Aborted');
        return;
      }
      const client = await createDataClient();
      // Wide on purpose: deleting a base whose id predates the rule is half of
      // the standing recovery path (create-new + migrate + delete the old one).
      await client.request('DELETE', `/v1/bases/${pathSegment(id)}${opts.purge ? '?purge=true' : ''}`);
      // Honors --json like every other command on this namespace: the flag is
      // advertised on the namespace root, so a script that pipes this to `jq`
      // must not get prose.
      printOutput({ id, deleted: true, purged: opts.purge === true }, outputFormat(this));
    });

  // --- Environment commands ---

  bases
    .command('create-preview <origin-id>')
    .description(
      'Create a preview base by cloning the config of another base. The origin may be a production base or another preview; the new preview id is <origin-id>--<name>, linked to the origin it was cloned from (so a preview of a preview promotes through that origin, never straight to production)',
    )
    .requiredOption('--name <name>', 'Preview base name')
    .option('--description <desc>', 'Description')
    .option(
      '--integrations <mode>',
      'enabled (default) | disabled. `disabled` makes this preview a seeded, production-safe agent-eval target: every external integration edge is inert and it behaves natively',
    )
    .option(
      '--create-only',
      'Fail with 409 if the derived preview id already exists, instead of re-applying config onto it. Use for per-session ephemeral bases, where landing on a live sibling would corrupt both runs',
    )
    .action(async function (
      this: Command,
      originId: string,
      opts: { name: string; description?: string; integrations?: string; createOnly?: boolean },
    ) {
      const integrations = parseEnum('--integrations', opts.integrations, ['enabled', 'disabled'] as const);
      const client = await createDataClient();
      printOutput(
        // `pathSegment`, not `basePathSegment`: the id at fault here is the
        // ORIGIN's, which is immutable and not the caller's to re-spell. The API's
        // `assertDerivablePreviewId` answers that case with the remedy instead of
        // the charset, and refusing locally would replace it with a rule the
        // caller cannot act on.
        await client.request('POST', `/v1/${pathSegment(originId, 'origin base id')}/preview`, {
          name: opts.name,
          description: opts.description,
          ...(integrations !== undefined ? { integrations } : {}),
          ...(opts.createOnly ? { create_only: true } : {}),
        }),
        outputFormat(this),
      );
    });

  bases
    .command('list-previews <origin-id>')
    .description('List preview bases cloned from a base (production or preview)')
    .action(async function (this: Command, originId: string) {
      const client = await createDataClient();
      const path = `/v1/${pathSegment(originId, 'origin base id')}/previews`;
      printOutput(
        await client.collectPages((cursor) => pageOf(path, cursor)),
        outputFormat(this),
      );
    });

  bases
    .command('promote <production-id>')
    .description('Promote config from a preview base to production (human-only)')
    .requiredOption('--from <preview-id>', 'Source preview base id')
    .option('--dry-run', 'Show what would change without applying')
    .option('--record-types <ids>', 'Comma-separated record type ids to promote', splitList)
    .option('--triggers <ids>', 'Comma-separated trigger ids to promote', splitList)
    .option('--inbound-webhooks <ids>', 'Comma-separated inbound webhook ids to promote', splitList)
    .option('--file-types <ids>', 'Comma-separated file type ids to promote', splitList)
    .action(async function (
      this: Command,
      productionId: string,
      opts: {
        from: string;
        dryRun?: boolean;
        recordTypes?: string[];
        triggers?: string[];
        inboundWebhooks?: string[];
        fileTypes?: string[];
      },
    ) {
      const client = await createDataClient();
      const data = await client.request<{
        source_credential_warnings?: Array<{ message: string }>;
        unresolved_credentials?: Array<{ message: string }>;
        // WayAI's own addition to the promote response — organization-linked credentials
        // are re-linked onto production after the promotion, and only WayAI can do that.
        credential_links?: { relinked: string[]; skipped: Array<{ name: string; reason: string }> };
      }>('POST', `/v1/${pathSegment(productionId, 'production base id')}/promote`, {
        source_base_id: opts.from,
        dry_run: opts.dryRun ?? false,
        record_types: opts.recordTypes,
        triggers: opts.triggers,
        inbound_webhooks: opts.inboundWebhooks,
        file_types: opts.fileTypes,
      });
      printOutput(data, outputFormat(this));
      // Two advisories, and they are about DIFFERENT things — a blanket "credentials are not carried
      // over" would be false for the second and would leave an operator to discover the difference
      // from a broken production source. Both print to stderr so machine-readable stdout stays clean.
      //
      // These embed tenant-authored record-type and source names, and this is the one path here that
      // prints server strings outside `printOutput`, so they sanitize explicitly rather than
      // inheriting it.
      const warnings = data?.source_credential_warnings ?? [];
      if (warnings.length > 0) {
        console.warn(
          '\nINLINE external-source secrets are NOT carried over by promotion — set these on production before the source will work:',
        );
        for (const w of warnings) console.warn(`  - ${sanitizeTerminalText(w.message)}`);
      }
      // A `credential:<name>` reference IS carried, for credentials the preview opted in — so what
      // is listed here is what this promotion will not have published. Silence would be the worst
      // outcome: the operator would set the credential by hand on production, leave the preview's
      // copy syncing, and have the next promotion overwrite it.
      const unresolved = data?.unresolved_credentials ?? [];
      if (unresolved.length > 0) {
        console.warn(
          '\nReferenced credentials production will still lack after this promotion:',
        );
        for (const w of unresolved) console.warn(`  - ${sanitizeTerminalText(w.message)}`);
      }
      // Organization-linked credentials are never published by a promotion — they are
      // re-linked afterwards, and only WayAI can do that. A skip here is the one place an
      // operator learns production is missing one: the far side reports nothing about it,
      // because from its side nothing went wrong.
      const links = data?.credential_links;
      if (links && links.skipped.length > 0) {
        console.warn('\nOrganization credentials NOT re-linked onto production:');
        for (const s of links.skipped) {
          console.warn(`  - ${sanitizeTerminalText(s.name)}: ${sanitizeTerminalText(s.reason)}`);
        }
      }
    });

  bases
    .command('rollback <production-id>')
    .description('Roll back a promotion (human-only)')
    .requiredOption('--promotion <promotion-id>', 'Promotion id to roll back')
    .action(async function (this: Command, productionId: string, opts: { promotion: string }) {
      const client = await createDataClient();
      printOutput(
        await client.request('POST', `/v1/${pathSegment(productionId, 'production base id')}/promote/rollback`, {
          promotion_id: opts.promotion,
        }),
        outputFormat(this),
      );
    });

  bases
    .command('promotions <production-id>')
    .description('List promotion history for a production base')
    .action(async function (this: Command, productionId: string) {
      const client = await createDataClient();
      const path = `/v1/${pathSegment(productionId, 'production base id')}/promotions`;
      printOutput(
        await client.collectPages((cursor) => pageOf(path, cursor)),
        outputFormat(this),
      );
    });

  // --- Worktree scope, base axis (DEPRECATED aliases of `wayai use` / `wayai unbind`) ---
  //
  // The scope is one set-valued file spanning every subtree, and `wayai use
  // bases/<id>` / `wayai unbind bases/<id>` are the commands that maintain it.
  // These two stay because
  // they are a published CLI surface with a machine-readable output shape that
  // scripts consume: they delegate to the same store and keep that shape
  // verbatim — `use` replaces the bases axis (the single-binding semantics it
  // has always had) and `unbind` clears only the bases axis, never another.

  bases
    .command('use <base>')
    .description(
      'Deprecated alias of `wayai use bases/<id>` — scope this worktree to a base',
    )
    .action(async function (this: Command, selector: string) {
      const baseId = resolveBaseSelectorToId(findGitRoot(), selector);
      const previous = readScope().bases;
      const changed = previous.length !== 1 || previous[0] !== baseId;
      if (changed) {
        try {
          setScopeAxis('bases', [baseId]);
        } catch (err) {
          // Not in a git repo, an unwritable git dir, or a selector the store
          // refuses (`..`) — user-actionable conditions, so they exit cleanly
          // rather than reaching Sentry as a crash.
          throw expected(err instanceof Error ? err.message : String(err));
        }
      }
      if (outputFormat(this) === 'json') {
        printOutput(
          { base_id: baseId, previous_base_id: previous[0] ?? null, changed },
          'json',
        );
        return;
      }
      if (!changed) {
        console.log(`Worktree already bound to base ${baseId}.`);
      } else {
        console.log(
          previous.length > 0
            ? `Worktree rebound: ${previous.join(', ')} -> ${baseId}.`
            : `Worktree bound to base ${baseId}.`,
        );
      }
    });

  bases
    .command('unbind')
    .description('Deprecated alias of `wayai unbind bases/<id>` — clear the base scope')
    .action(async function (this: Command) {
      const previous = readScope().bases;
      if (previous.length > 0) setScopeAxis('bases', []);
      if (outputFormat(this) === 'json') {
        printOutput(
          { previous_base_id: previous[0] ?? null, cleared: previous.length > 0 },
          'json',
        );
        return;
      }
      console.log(
        previous.length > 0
          ? `Worktree unbound from base (was: ${previous.join(', ')}).`
          : 'No base binding to clear.',
      );
    });

  // --- Namespaced operational surfaces ---
  //
  // Each of these was a top-level word on the standalone Data CLI and collides
  // with a concept WayAI already owns under a different command name, so clause 3
  // puts it here rather than at the top level. They live in sibling
  // modules purely for size; the namespace they belong to is this one.
  //
  // `report` is the exception that is NOT about wording: `wayai report` and
  // `wayai bases report` file to two genuinely different queues, and they merge
  // into one command only when the queues do.
  bases.addCommand(buildBasesTokensCommand());
  bases.addCommand(buildBasesCredentialsCommand());
  bases.addCommand(buildBasesSqlCommand());
  bases.addCommand(buildBasesImportCommand());
  bases.addCommand(buildBasesBatchCommand());
  bases.addCommand(buildBasesProvidersCommand());
  bases.addCommand(buildBasesReportCommand());

  return bases;
}
